import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export type MaintenanceBannerType = 'Info' | 'Warning' | 'Critical';

export interface MaintenanceBanner {
  active: boolean;
  type: MaintenanceBannerType;
  /** EWM-607 legacy single-language message, kept as a fallback. */
  message: string;
  /** EWM-758: per-language messages; the UI picks by active locale. */
  messageEn: string;
  messageDe: string;
}

/**
 * EWM-758: resolve the banner text for the active UI language. Picks the message
 * matching the locale (de-* -> German, otherwise English), then falls back to the
 * other language, and finally to the legacy single-language `message`, so a banner
 * configured before EWM-758 — or in only one language — still renders.
 */
export const resolveBannerMessage = (banner: MaintenanceBanner, locale: string): string => {
  const preferGerman = locale.toLowerCase().startsWith('de');
  const primary = preferGerman ? banner.messageDe : banner.messageEn;
  const secondary = preferGerman ? banner.messageEn : banner.messageDe;
  return primary || secondary || banner.message || '';
};

const MAINTENANCE_BANNER_POLL_MS = 60_000;

/**
 * EWM-607: polls the public maintenance-banner endpoint so deployment notices
 * activate/deactivate within ~60s mid-session, no page reload required.
 */
export const useMaintenanceBanner = () =>
  useQuery<MaintenanceBanner>({
    queryKey: ['maintenance-banner'],
    queryFn: async () => {
      const { data } = await axios.get<MaintenanceBanner>('/api/v1/system/maintenance-banner');
      return data;
    },
    refetchInterval: MAINTENANCE_BANNER_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: MAINTENANCE_BANNER_POLL_MS / 2,
    retry: false,
  });
